import React, { type MouseEvent, useState, useCallback } from "react";
import Image from "@atlaskit/image";
import { NavLogo } from "@atlassian/navigation-system";
import { Anchor, xcss } from "@atlaskit/primitives";
import { LogoProps, RovoIcon, RovoLogo } from "@atlaskit/logo";
import { useIntl } from "react-intl";
import { token } from "@atlaskit/tokens";
import { Site } from "../../hooks/useSite";
import { useNtpThemeFile, useNtpThemeFileMetadata } from "../../clients/theme-client/themeClient";
import { ThemeFileType } from "../../clients/theme-client/types";
import { messages } from "./messages";
import { useSendAnalyticEvent } from "../../analytics/analyticsClient";
import { useCurrentScreenName } from "../../hooks/useCurrentScreenName";
import { ANALYTICS_UI_SUBJECTS } from "../../analytics/analyticsEvents";
import { useMatch } from "react-router-dom";
import { useExtensionNavigation } from "../../hooks/useExtensionNavigation";
import { UserAccount } from "../../clients/gateway-client/types";
import { useQueryInvalidationBroadcast } from "../../hooks/useQueryInvalidationBroadcast";
import { useEntitlementCheck } from "../../hooks/useEntitlementCheck";

const logoStyles = xcss({
  height: token("space.300"),
});

type LogoType = "default" | "custom";

export const ExtensionTopBarCompanyLogo = ({
  userAccount,
  site,
}: {
  userAccount: UserAccount;
  site: Site | undefined;
}): React.JSX.Element => {
  const sendAnalyticEvent = useSendAnalyticEvent();
  const currentScreenName = useCurrentScreenName();

  const handleLogoClick = useCallback(
    (e: MouseEvent, logoType: LogoType): void => {
      // Prevent anchor's default navigation to avoid full page reload when using React Router
      e.preventDefault();

      // https://data-portal.internal.atlassian.com/analytics/registry/86536
      sendAnalyticEvent({
        eventType: "ui",
        event: {
          action: "click",
          actionSubject: ANALYTICS_UI_SUBJECTS.NAV_BAR_LOGO,
          source: currentScreenName,
          attributes: { logoType },
        },
        userId: userAccount.accountId,
      });
    },
    [currentScreenName, sendAnalyticEvent, userAccount.accountId],
  );

  if (!site) {
    return <DefaultCompanyIcon isRovoLLMEnabled={false} onLogoClick={handleLogoClick} />;
  }

  return <CompanyIcon onLogoClick={handleLogoClick} site={site} />;
};

const CompanyIcon = ({
  site,
  onLogoClick,
}: {
  site: Site;
  onLogoClick: (e: MouseEvent, logoType: LogoType) => void;
}): React.JSX.Element => {
  const { data: themeFileMetadata, isLoading } = useNtpThemeFileMetadata({
    siteId: site.workspace.cloudId,
  });
  const { isRovoLLMEnabled } = useEntitlementCheck();
  useQueryInvalidationBroadcast(["getNtpThemeFileMetadata", site.workspace.cloudId], true);

  if (isLoading) {
    return <></>;
  }

  const logoThemeMetadata = themeFileMetadata?.find((m) => m.themeFileType === ThemeFileType.LOGO);

  if (logoThemeMetadata) {
    return (
      <CustomCompanyIcon
        isRovoLLMEnabled={isRovoLLMEnabled}
        onLogoClick={onLogoClick}
        s3Etag={logoThemeMetadata.s3Etag}
        site={site}
      />
    );
  }

  return <DefaultCompanyIcon isRovoLLMEnabled={isRovoLLMEnabled} onLogoClick={onLogoClick} />;
};

const DefaultCompanyIcon = ({
  onLogoClick,
  isRovoLLMEnabled,
}: {
  onLogoClick: (e: MouseEvent, logoType: LogoType) => void;
  isRovoLLMEnabled: boolean;
}): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const isOnHomePage = !!useMatch("/extension");
  const { navigateToChat, navigateToHome } = useExtensionNavigation();
  const rovoIcon = (props: LogoProps): React.JSX.Element => <RovoIcon shouldUseHexLogo {...props} />;
  const rovoLogo = (props: LogoProps): React.JSX.Element => <RovoLogo shouldUseHexLogo {...props} />;

  const handleDefaultLogoClick = (e: MouseEvent): void => {
    onLogoClick(e, "default");
    if (isOnHomePage && isRovoLLMEnabled) {
      navigateToChat();
    } else {
      navigateToHome();
    }
  };

  return (
    <NavLogo
      href="/extension"
      icon={rovoIcon}
      label={formatMessage(messages.homeLabel)}
      logo={rovoLogo}
      onClick={handleDefaultLogoClick}
    />
  );
};

const CustomCompanyIcon = ({
  s3Etag,
  site,
  onLogoClick,
  isRovoLLMEnabled,
}: {
  s3Etag: string;
  site: Site;
  onLogoClick: (e: MouseEvent, logoType: LogoType) => void;
  isRovoLLMEnabled: boolean;
}): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const { navigateToHome } = useExtensionNavigation();

  const handleCustomLogoClick = (e: MouseEvent): void => {
    onLogoClick(e, "custom");
    navigateToHome();
  };

  const { data: logoFileData, isLoading } = useNtpThemeFile({
    siteId: site.workspace.cloudId,
    fileType: ThemeFileType.LOGO,
    s3Etag,
  });
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  if (isLoading) {
    return <></>;
  }

  if (!logoFileData?.url) {
    // Fallback on default
    return <DefaultCompanyIcon isRovoLLMEnabled={isRovoLLMEnabled} onLogoClick={onLogoClick} />;
  }

  const ImageComponent = (props: LogoProps): React.JSX.Element =>
    isImageLoaded ? (
      <Image
        alt={formatMessage(messages.companyLogoAlt)}
        src={logoFileData.url}
        style={{ height: token("space.300") }}
        {...props}
      />
    ) : (
      <Image
        alt={formatMessage(messages.companyLogoAlt)}
        onLoad={() => setIsImageLoaded(true)}
        src={logoFileData.url}
        style={{ display: "none" }}
        {...props}
      />
    );

  return (
    <Anchor
      aria-label={formatMessage(messages.homeLabel)}
      href="/extension"
      onClick={handleCustomLogoClick}
      xcss={logoStyles}
    >
      <ImageComponent />
    </Anchor>
  );
};
